import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import axios from 'axios';

import {
  uploadFileStart,
  uploadFile,
  uploadProgress,
  uploadFinished,
  uploadError,
  uploadRetryCount,
  wholeUploadFinished,
  filesUploadHelper,
} from 'actions/UploadActions';

function Upload() {
  const intl = useIntl();
  const dispatch = useDispatch();

  // Redux State Hooks
  const webApp = useSelector((state) => state.webApp);
  const user = useSelector((state) => state.login.user);
  const upload = useSelector((state) => state.upload);

  // Local State & Refs
  const [isOver, setIsOver] = useState(false);
  const [retryTimeoutPending, setRetryTimeoutPending] = useState(null);
  const isUploadingRef = useRef(false);
  const uploadingFileRef = useRef(null);

  const onDropHelper = useCallback(filesUploadHelper(dispatch), [dispatch]);

  // Upload Logic (ported from componentDidUpdate)
  useEffect(() => {
    // 1. Check if current upload is finished
    if (isUploadingRef.current && upload?.files) {
      const currentFile = upload.files.find((f) => f.file === uploadingFileRef.current);
      if (currentFile && (currentFile.isUploaded || currentFile.networkError || currentFile.error !== 0)) {
        isUploadingRef.current = false;
        uploadingFileRef.current = null;
      }
    }

    // 2. Logic to start new uploads
    if (!uploadingFileRef.current && !retryTimeoutPending && webApp.isOnline && upload?.files) {
      for (const file of upload.files) {
        if (!file.isUploading && !file.isUploaded && file.error === 0) {
          if (file.networkError) {
            const timeout = setTimeout(
              () => {
                setRetryTimeoutPending(null);
                startUploadAction(file.file, file.uploadLink, file.args, file.retryCount);
              },
              file.retryCount * file.retryCount * 1000
            );
            setRetryTimeoutPending(timeout);
          } else {
            isUploadingRef.current = true;
            uploadingFileRef.current = file.file;
            startUploadAction(file.file, file.uploadLink, file.args);
          }
          break;
        }
      }
    }
  }, [upload.files, webApp.isOnline, retryTimeoutPending]);

  // Cleanup on Unmount
  useEffect(() => {
    return () => {
      if (retryTimeoutPending) clearTimeout(retryTimeoutPending);
    };
  }, [retryTimeoutPending]);

  const startUploadAction = (file, uploadLink, args, retryAttempts = 0) => {
    const retryAttemptsMax = 10;
    const source = axios.CancelToken.source();

    const progressCallback = (event) => dispatch(uploadProgress(event, file));
    const finishedCallback = (resp) => dispatch(uploadFinished(file, resp));
    const errorCallback = (err) => {
      if ((!err.response || !err.response.status) && retryAttempts < retryAttemptsMax) {
        dispatch(uploadRetryCount(file, retryAttempts + 1));
      } else {
        dispatch(uploadError(file, err));
      }
    };

    dispatch(uploadFileStart(file, retryAttempts));
    dispatch(uploadFile(uploadLink, file, args, progressCallback, finishedCallback, errorCallback, source.token));
  };

  // --- Native Drag & Drop Handler ---
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Check if it is a file upload
    const isFileDrag = e.dataTransfer.types.includes('Files');

    // Only display if they are files AND the user is logged in
    if (user.logged && isFileDrag) {
      setIsOver(true);
    } else {
      // If an internal element is pulled, we ensure that the upload overlay remains closed
      setIsOver(false);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOver(false);

    if (user.logged && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      // Converts FileList to array
      const files = Array.from(e.dataTransfer.files);
      onDropHelper(files, upload.context.uploadLink, upload.context.data);
    }
  };

  // --- Render Logic ---
  // We use a global listener on the window or an invisible overlay here.
  // So that the drop works everywhere, just like in our old code
  useEffect(() => {
    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('drop', handleDrop);
    return () => {
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('drop', handleDrop);
    };
  }, [user.logged, upload.context]);

  if (!isOver || !user.logged) return null;

  const style = {
    position: 'fixed',
    top: 0,
    left: 0,
    height: '100vh',
    width: '100vw',
    background: 'rgba(13,24,35,0.95)',
    zIndex: 9999,
    color: '#fff',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    pointerEvents: 'all',
  };

  return (
    <div onDragLeave={handleDragLeave} style={style}>
      <i className="fa fa-cloud-upload" style={{ fontSize: '20em' }} />
      <h1>Drop your files here</h1>
    </div>
  );
}

export default Upload;
